/**
 * Curated starter prompts for empty Copilot chat sessions.
 */
import React, { useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Colors } from '@styles/index';

export interface ChatSuggestion {
  query: string;
  category: string;
}

export interface ChatSuggestionPromptsProps {
  suggestions: ChatSuggestion[];
  onSuggestionClick: (query: string) => void;
  isLoading?: boolean;
  variant?: 'panel' | 'overlay';
}

const GENERIC_CATEGORIES = new Set(['', 'explore', 'general']);

const isMeaningfulCategory = (category: string): boolean =>
  !GENERIC_CATEGORIES.has(category.trim().toLowerCase());

const SkeletonRow: React.FC = () => (
  <Skeleton
    variant="rounded"
    height={44}
    sx={{ borderRadius: '10px', bgcolor: 'rgba(15, 23, 42, 0.04)' }}
  />
);

export const ChatSuggestionPrompts: React.FC<ChatSuggestionPromptsProps> = ({
  suggestions,
  onSuggestionClick,
  isLoading = false,
  variant = 'panel',
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, ChatSuggestion[]>();

    suggestions.forEach((item) => {
      const category = item.category?.trim() || '';
      const key = isMeaningfulCategory(category) ? category : '';
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    });

    return Array.from(map.entries());
  }, [suggestions]);

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Skeleton variant="text" width="72%" height={28} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="88%" height={18} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </Box>
      </Box>
    );
  }

  if (!suggestions.length) {
    return null;
  }

  const isOverlay = variant === 'overlay';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: isOverlay ? 480 : '100%',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isOverlay ? 'center' : 'flex-start',
        minHeight: isOverlay ? '100%' : 'auto',
        py: isOverlay ? 1 : 0,
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{
            fontSize: isOverlay ? '1rem' : '0.9375rem',
            fontWeight: 600,
            color: '#111827',
            letterSpacing: '-0.02em',
            lineHeight: 1.35,
            mb: 0.5,
          }}
        >
          What would you like to explore?
        </Typography>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            color: '#6B7280',
            lineHeight: 1.5,
          }}
        >
          Pick a starter prompt or type your own question below
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {grouped.map(([category, items]) => (
          <Box key={category || 'default'}>
            {isMeaningfulCategory(category) && (
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: '#94A3B8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 1,
                }}
              >
                {category}
              </Typography>
            )}

            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              {items.map((item, index) => (
                <Box
                  component="li"
                  key={`${category}-${index}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSuggestionClick(item.query)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSuggestionClick(item.query);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                    backgroundColor: Colors.white,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#F8FAFC',
                      borderColor: '#CBD5E1',
                      '& .suggestion-chevron': {
                        color: '#0078D4',
                        transform: 'translateX(2px)',
                      },
                    },
                    '&:active': {
                      backgroundColor: '#F1F5F9',
                    },
                    '&:focus-visible': {
                      outline: '2px solid rgba(0, 120, 212, 0.35)',
                      outlineOffset: 1,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#1F2937',
                      lineHeight: 1.5,
                    }}
                  >
                    {item.query}
                  </Typography>
                  <ChevronRightRoundedIcon
                    className="suggestion-chevron"
                    sx={{
                      fontSize: 20,
                      color: '#CBD5E1',
                      flexShrink: 0,
                      transition: 'color 0.15s ease, transform 0.15s ease',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChatSuggestionPrompts;
